import type { UIMessage } from "../../types/chat";
import { formatMessageTime } from "../../lib/utils";
import { AIAvatarRow } from "./AIAvatarRow";
import { MarkdownContent } from "./MarkdownContent";
import { CandidateCards } from "./CandidateMessageWrapper";
import { Button } from "../ui/Button";
import RestartLinear from "~icons/solar/restart-linear";

interface AIMessageBubbleProps {
  message: UIMessage;
  onRetry?: () => void;
}

/** Glass card, no tail on the bottom-left corner (v3). */
const BUBBLE =
  "glass max-w-[88%] min-w-0 rounded-[20px] rounded-bl-[4px] p-4 text-sm leading-relaxed text-ink shadow-card sm:max-w-[80%]";

export function AIMessageBubble({ message, onRetry }: AIMessageBubbleProps) {
  const isStreaming = message.status === "streaming" && message.content !== "";
  const isError = message.status === "error";

  // Empty streaming placeholder is rendered by MessageList as the scouting animal.
  if (message.status === "streaming" && message.content === "") return null;

  return (
    <AIAvatarRow>
      {isError ? (
        <div
          role="alert"
          className="flex max-w-[88%] items-start gap-3 rounded-[20px] rounded-bl-[4px] border border-error/20 bg-error-light px-4 py-3 sm:max-w-[80%]"
        >
          <p className="min-w-0 flex-1 text-sm text-error">{message.content}</p>
          {onRetry && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={RestartLinear}
              onClick={onRetry}
              className="shrink-0"
            >
              Retry
            </Button>
          )}
        </div>
      ) : (
        <div className={BUBBLE}>
          <MarkdownContent content={message.content} />
          {isStreaming && (
            <span
              aria-hidden="true"
              className="ml-1 inline-block size-2 translate-y-px rounded-full bg-primary motion-safe:animate-pulse"
            />
          )}

          {/* Top 4 candidate cards + "View all (N)" once the result is complete */}
          {message.jdPath && message.status === "complete" && (
            <CandidateCards jdPath={message.jdPath} />
          )}

          {!isStreaming && message.timestamp && (
            <span className="mt-2 block text-left text-xs text-ink-faint">
              {formatMessageTime(message.timestamp)}
            </span>
          )}
        </div>
      )}
    </AIAvatarRow>
  );
}
